import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DASHBOARD_WIDGET_REGISTRY,
  DASHBOARD_SECTION_LABELS,
  defaultDashboardSection,
  ROLE_LABELS,
  STAFF_ROLES,
  dashboardWidgetKeySchema,
  type DashboardTemplateRole,
  type DashboardTemplateWidget,
  type DashboardWidgetKey,
  type DashboardSectionKey,
} from '@1financial/shared';
import { getAccessToken, authenticatedFetch as fetch } from '../../lib/auth-context.js';
import { httpError } from '../../lib/http-error.js';
import { useCapabilities } from '../../hooks/useCapabilities.js';
import { ForbiddenPage } from '../../components/ForbiddenPage.js';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';

type TemplateRow = { role: DashboardTemplateRole; defaults: DashboardTemplateWidget[]; draft: { version: number } | null; published: { version: number } | null };
type TemplatesResponse = { roles: TemplateRow[] };
const input = 'bg-bg-base border border-border rounded-lg px-2 py-1.5 text-sm text-white disabled:opacity-50';

export function DashboardBuilderPage() {
  const { canDo } = useCapabilities();
  const queryClient = useQueryClient();
  const [role, setRole] = useState<DashboardTemplateRole>('CEO');
  const [defaultSection, setDefaultSection] = useState<DashboardSectionKey>('myTeamWork');
  const [widgets, setWidgets] = useState<DashboardTemplateWidget[]>([]);
  const [addKey, setAddKey] = useState<DashboardWidgetKey>('myTasks');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const canManage = canDo('dashboard.configure');

  const templates = useQuery({
    queryKey: ['admin-dashboard-templates'],
    enabled: canManage,
    queryFn: async () => {
      const res = await fetch('/v1/admin/dashboard/templates', { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await httpError(res);
      return (await res.json() as { data: TemplatesResponse }).data;
    },
  });
  const selected = templates.data?.roles.find((entry) => entry.role === role);
  useEffect(() => {
    if (!selected) return;
    const draft = templates.data?.roles.find((entry) => entry.role === role)?.draft as unknown as { widgets?: DashboardTemplateWidget[]; defaultSection?: DashboardSectionKey } | null;
    const published = templates.data?.roles.find((entry) => entry.role === role)?.published as unknown as { widgets?: DashboardTemplateWidget[]; defaultSection?: DashboardSectionKey } | null;
    setWidgets(structuredClone(draft?.widgets ?? published?.widgets ?? selected.defaults));
    setDefaultSection(draft?.defaultSection ?? published?.defaultSection ?? defaultDashboardSection(role));
    setError(null); setNotice(null);
  }, [role, templates.data, selected]);

  function refresh() { void queryClient.invalidateQueries({ queryKey: ['admin-dashboard-templates'] }); }
  const save = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/v1/admin/dashboard/templates/${role}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` }, body: JSON.stringify({ defaultSection, widgets }) });
      if (!res.ok) throw await httpError(res);
    },
    onSuccess: () => { setNotice('Draft saved. Publish when ready.'); setError(null); refresh(); },
    onError: (e) => { setError(e.message); setNotice(null); },
  });
  const publish = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/v1/admin/dashboard/templates/${role}/publish`, { method: 'POST', headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await httpError(res);
      return (await res.json() as { data: { version: number } }).data;
    },
    onSuccess: (data) => { setNotice(`Published ${ROLE_LABELS[role]} dashboard v${data.version}.`); setError(null); refresh(); },
    onError: (e) => { setError(e.message); setNotice(null); },
  });
  const reset = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/v1/admin/dashboard/templates/${role}/reset`, { method: 'POST', headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await httpError(res);
    },
    onSuccess: () => { setNotice('Draft reset to the OPS recommended default.'); setError(null); refresh(); },
    onError: (e) => { setError(e.message); setNotice(null); },
  });

  function patch(index: number, value: Partial<DashboardTemplateWidget>) {
    setWidgets((current) => current.map((entry, i) => i === index ? { ...entry, ...value } : entry));
    setNotice(null);
  }
  function move(index: number, direction: -1 | 1) {
    setWidgets((current) => {
      const target = index + direction;
      if (target < 0 || target >= current.length) return current;
      const next = [...current]; [next[index], next[target]] = [next[target]!, next[index]!]; return next;
    });
    setNotice(null);
  }
  const busy = save.isPending || publish.isPending || reset.isPending;

  if (!canManage) return <ForbiddenPage />;
  return (
    <div className="space-y-4">
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-white">Dashboard Builder</h2>
          <p className="text-sm text-muted">Configure safe role templates. Drafts do not change live dashboards until published.</p>
        </div>
        <span className="text-xs text-muted">{selected?.draft ? `Draft v${selected.draft.version}` : selected?.published ? `Published v${selected.published.version}` : 'OPS default'}</span>
      </header>

      <div className="flex flex-wrap gap-2">
        {STAFF_ROLES.map((item) => <button key={item} onClick={() => setRole(item)} className={`px-3 py-1.5 rounded-lg border text-sm ${role === item ? 'border-accent text-white bg-bg-hover' : 'border-border text-muted hover:text-white'}`}>{ROLE_LABELS[item]}</button>)}
      </div>

      <label className="text-xs text-muted flex items-center gap-2">Default landing section
        <select value={defaultSection} onChange={(e) => { setDefaultSection(e.target.value as DashboardSectionKey); setNotice(null); }} className={input}>
          {Object.entries(DASHBOARD_SECTION_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
        </select>
      </label>

      <section className="bg-bg-panel border border-border rounded-xl p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-medium text-white">{ROLE_LABELS[role]} template · {widgets.length} widgets</h3>
          <div className="flex flex-wrap gap-2">
            <button disabled={busy} onClick={() => reset.mutate()} className="text-xs border border-border rounded-lg px-3 py-1.5 text-muted hover:text-white">Reset draft</button>
            <button disabled={busy} onClick={() => save.mutate()} className="text-xs border border-border rounded-lg px-3 py-1.5 text-white">Save draft</button>
            <button disabled={busy} onClick={() => publish.mutate()} className="text-xs bg-accent text-accent-fg rounded-lg px-3 py-1.5">Publish</button>
          </div>
        </div>
        {error && <p role="alert" className="text-sm text-rose-400">{error}</p>}
        {notice && <p role="status" className="text-sm text-emerald-300">{notice}</p>}
        <ul className="space-y-2">
          {widgets.map((entry, index) => {
            const definition = DASHBOARD_WIDGET_REGISTRY[entry.key];
            return (
              <li key={entry.key} className="border border-border rounded-lg p-3 grid grid-cols-1 xl:grid-cols-[1.5fr_.7fr_.7fr_.7fr_1fr_auto] gap-3 items-center">
                <div>
                  <div className="text-sm text-white">{definition.label}</div>
                  <div className="text-xs text-muted">{definition.description} · {definition.scope} scope</div>
                </div>
                <label className="text-xs text-muted flex items-center gap-2"><input type="checkbox" checked={entry.visible} onChange={(e) => patch(index, { visible: e.target.checked })} /> Visible</label>
                <label className="text-xs text-muted flex items-center gap-2"><input type="checkbox" checked={entry.required} onChange={(e) => patch(index, { required: e.target.checked, visible: e.target.checked ? true : entry.visible })} /> Required</label>
                <label className="text-xs text-muted">Width<select value={entry.size} onChange={(e) => patch(index, { size: e.target.value as 'half' | 'full' })} className={`${input} mt-1 w-full`}><option value="half">Half</option><option value="full">Full</option></select></label>
                <label className="text-xs text-muted">List limit<input type="number" min={definition.minListLimit} max={definition.maxListLimit} value={entry.listLimit} onChange={(e) => patch(index, { listLimit: Number(e.target.value) })} className={`${input} mt-1 w-full`} /></label>
                <div className="flex items-center gap-2 justify-end">
                  <button aria-label={`Move ${definition.label} up`} disabled={index === 0} onClick={() => move(index, -1)} className="text-muted hover:text-white disabled:opacity-30"><ArrowUp size={15} /></button>
                  <button aria-label={`Move ${definition.label} down`} disabled={index === widgets.length - 1} onClick={() => move(index, 1)} className="text-muted hover:text-white disabled:opacity-30"><ArrowDown size={15} /></button>
                  <button aria-label={`Remove ${definition.label}`} onClick={() => setWidgets((current) => current.filter((_, i) => i !== index))} className="text-rose-400"><Trash2 size={15} /></button>
                </div>
              </li>
            );
          })}
        </ul>
        <div className="flex flex-wrap items-end gap-2 pt-2">
          <label className="text-xs text-muted">Add widget
            <select value={addKey} onChange={(e) => setAddKey(e.target.value as DashboardWidgetKey)} className={`${input} ml-2`}>
              {dashboardWidgetKeySchema.options.filter((key) => !widgets.some((entry) => entry.key === key) && (role !== 'Staff' || DASHBOARD_WIDGET_REGISTRY[key].view !== 'full')).map((key) => <option key={key} value={key}>{DASHBOARD_WIDGET_REGISTRY[key].label}</option>)}
            </select>
          </label>
          <button disabled={widgets.some((entry) => entry.key === addKey)} onClick={() => { const d = DASHBOARD_WIDGET_REGISTRY[addKey]; setWidgets((current) => [...current, { key: addKey, visible: true, required: false, size: d.defaultSize, listLimit: d.defaultListLimit }]); }} className="inline-flex items-center gap-1 text-xs border border-border rounded-lg px-3 py-2 text-white disabled:opacity-40"><Plus size={13} /> Add</button>
        </div>
      </section>

      <section className="bg-bg-panel border border-border rounded-xl p-4">
        <h3 className="text-sm font-medium text-white mb-2">Preview as {ROLE_LABELS[role]}</h3>
        <p className="text-xs text-muted mb-3">Preview uses the current unsaved draft. Landing: {DASHBOARD_SECTION_LABELS[defaultSection]}. Required widgets cannot be hidden by the user.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {widgets.filter((entry) => entry.visible).map((entry) => <div key={entry.key} className={`border border-border rounded-lg p-3 ${entry.size === 'full' ? 'md:col-span-2' : ''}`}>
            <div className="text-sm text-white">{DASHBOARD_WIDGET_REGISTRY[entry.key].label}</div>
            <div className="text-xs text-muted">{entry.required ? 'Required · ' : ''}{entry.size} width · up to {entry.listLimit} rows</div>
          </div>)}
        </div>
      </section>
    </div>
  );
}
